
import CrudMultiSelect from '@gid/vue-common/components/filters/CrudMultiSelect.vue';
import { Vue, Component, Prop } from 'vue-property-decorator';
import DocumentLabel from './DocumentLabel.vue';
import { CreateQueryParams, QueryJoin } from '@dataui/crud-request';
import { AccountingDocument } from '@gid/models';

@Component({
  components: {
    DocumentLabel,
    CrudMultiSelect,
  },
})
export default class DocumentSelector extends Vue {
  @Prop({ default: null }) value!: any;
  @Prop() queryParams!: CreateQueryParams;

  get model() {
    return AccountingDocument;
  }

  /**
   * Re-emit the selected document together with the side the user's search
   * text explicitly picked — or `undefined` when it picked neither.
   * vue-multiselect clears its search text only after the input event has
   * been handled, so reading it synchronously here still sees the query the
   * user selected under; that context would otherwise be lost.
   */
  onInput(document: any) {
    const multiselect = (this.$refs.crudSelect as Vue)?.$children?.[0] as any;
    const search: string | null = multiselect?.search ?? null;
    this.$emit('input', document, this.explicitSideFor(document, search));
  }

  /**
   * The side the search text UNAMBIGUOUSLY picked, or `undefined` for no
   * signal.
   *
   * Returning `undefined` rather than `false` is the whole point: the consumer
   * has to distinguish "the user chose the original side" from "the user gave
   * no hint", because only the latter may fall back to inference. Collapsing
   * the two is what made a (sent, paid) document impossible to add on its
   * original side — inference always said "cancellation" and there was no way
   * to outvote it. See ReconcilePayment.addDocument.
   */
  explicitSideFor(
    doc: {
      number: string;
      cancellationNumber?: string | null;
    },
    search?: string | null,
  ): boolean | undefined {
    // No cancellation number means only one side exists at all.
    if (!doc.cancellationNumber) return false;
    const normalisedSearch = (search || '').trim().toLowerCase();
    if (!normalisedSearch) return undefined;
    const matchesCancellation = doc.cancellationNumber
      .toLowerCase()
      .includes(normalisedSearch);
    const matchesOriginal = (doc.number || '')
      .toLowerCase()
      .includes(normalisedSearch);
    if (matchesCancellation && !matchesOriginal) return true;
    if (matchesOriginal && !matchesCancellation) return false;
    // Matched both (or neither — e.g. searched by account or job name):
    // no signal, let the consumer infer.
    return undefined;
  }

  /**
   * Whether the dropdown renders the doc in cancellation context (STT number,
   * cancellation status badge, inverted amount sign). Mirrors exactly what
   * ReconcilePayment.addDocument will do with the emitted value — an explicit
   * pick wins, otherwise the same inference the modal applies — so the preview
   * never disagrees with the row that gets added.
   */
  isCancellationMatch(
    doc: {
      number: string;
      cancellationNumber?: string | null;
      status?: string;
      cancellationStatus?: string | null;
    },
    search?: string | null,
  ): boolean {
    const explicit = this.explicitSideFor(doc, search);
    if (typeof explicit === 'boolean') return explicit;
    return doc.cancellationStatus === 'paid' && doc.status === 'sent';
  }
}
